// Chrome allows about 16 live WebGL contexts per page. map.remove frees a context only when
// garbage collection runs, so rapid remounts can pass that ceiling. WEBGL_lose_context frees the
// slot immediately.
import type { Map as MlMap } from "maplibre-gl";

const releasePatched = new WeakSet<MlMap>();

function loseWebglContext(canvas: HTMLCanvasElement | null): void {
  try {
    const gl = (canvas?.getContext("webgl2") ??
      canvas?.getContext("webgl")) as WebGLRenderingContext | null;
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    /* the browser already reclaimed it */
  }
}

export function removeMapFully(map: MlMap): void {
  let canvas: HTMLCanvasElement | null = null;
  try {
    canvas = map.getCanvas();
  } catch {
    /* already torn down */
  }
  try {
    map.remove();
  } catch {
    /* already torn down */
  }
  loseWebglContext(canvas);
}

// The React binding calls map.remove() itself. Lose the context after that
// call returns, while the canvas is still in hand. A second patch would wrap
// the wrapper and lose the context twice.
export function loseWebglContextAfterRemove(map: MlMap): void {
  if (releasePatched.has(map)) return;
  releasePatched.add(map);
  const original = map.remove.bind(map);
  map.remove = () => {
    let canvas: HTMLCanvasElement | null = null;
    try {
      canvas = map.getCanvas();
    } catch {
      /* already torn down */
    }
    try {
      original();
    } catch {
      /* already torn down */
    }
    loseWebglContext(canvas);
  };
}
